import { z } from 'zod';

/**
 * A FRANQUIA DE BAGAGEM que o fornecedor declara na tarifa, lida de
 * `fares.tariffDetails[]` — a ÚNICA leitura desse array como bagagem, e a única
 * fonte estruturada de peças/peso no caminho do voucher emitido.
 *
 * Existia só o par `fares.baggage` (número cru que MISTURA mão e despacho) +
 * `fares.checkedBaggage` (booleano), e por isso toda tela caía na frase genérica
 * "Despachada incluída" mesmo quando a fonte sabia dizer "1 peça de 23kg"
 * (issue #3305).
 *
 * Regra de ouro: NUNCA inventar número. Sem quantidade reconhecida na prosa,
 * `pieces`/`weightKg` ficam `null` e quem desenha volta à frase genérica.
 */
export const BaggageAllowanceLineSchema = z.object({
  /** Incluída na tarifa. `null` = o fornecedor não afirmou nem negou. */
  included: z.boolean().nullable(),
  /** Quantidade de VOLUMES; `null` quando a prosa não a diz. */
  pieces: z.number().nullable(),
  /** Limite por volume, em quilos; `null` quando a prosa não o diz. */
  weightKg: z.number().nullable(),
  /** A prosa do fornecedor, verbatim — sempre mais específica que a nossa. */
  text: z.string().nullable(),
});
export type BaggageAllowanceLine = z.infer<typeof BaggageAllowanceLineSchema>;

export const BaggageAllowanceSchema = z.object({
  checked: BaggageAllowanceLineSchema.nullable(),
  carry: BaggageAllowanceLineSchema.nullable(),
});
export type BaggageAllowance = z.infer<typeof BaggageAllowanceSchema>;

/** Um item de `fares.tariffDetails[]` como as duas fontes o entregam: uma regra
 *  tarifária por linha (`name` = LUGGAGE, HAND_LUGGAGE, REFUND, CHANGE, …), e as
 *  de bagagem dividem o array com as de reembolso/alteração/cancelamento. */
export interface TariffDetailLike {
  name?: string | null;
  description?: string | null;
  availability?: boolean | string | null;
}

type BaggageKind = 'checked' | 'carry';

/** `HAND_LUGGAGE` contém `LUGGAGE`: a mão é testada PRIMEIRO, senão toda bagagem
 *  de mão seria lida como despacho. */
function kindOf(name: string | null | undefined): BaggageKind | null {
  const n = name?.toUpperCase() ?? '';
  if (/HAND|CARRY|CABIN/.test(n)) return 'carry';
  return /LUGGAGE|BAGGAGE/.test(n) ? 'checked' : null;
}

/** `availability` chega booleano do voucher-api e como token do gateway de voo
 *  (`INCLUDED` / `NOT_AVAILABLE` / `NOT_INCLUDED`). `CHARGED` e qualquer outro
 *  token ficam `null`: bagagem paga à parte não é "sem bagagem". */
function includedFrom(availability: boolean | string | null | undefined): boolean | null {
  if (typeof availability === 'boolean') return availability;
  const token = availability?.toUpperCase().replaceAll(/[^A-Z]+/g, '_') ?? '';
  if (token === 'INCLUDED') return true;
  if (token === 'NOT_AVAILABLE' || token === 'NOT_INCLUDED' || token === 'UNAVAILABLE')
    return false;
  return null;
}

const norm = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '');

/** As grafias de quilo das três línguas da fonte (pt/en/es), abreviadas e por
 *  extenso. Lista ÚNICA de propósito: a mesma unidade que BLOQUEIA a leitura de
 *  peças é a que ABRE a de peso — separá-las deixaria "23 kilograms per piece"
 *  virar "23 peças" sem peso nenhum. */
const KILO = String.raw`(?:kgs?|quilogramas?|kilogramas?|quilogramos?|kilogramos?|kilograms?|quilos?|kilos?)`;

/** "1 peça", "2 peças", "1 piece", "2 checked bags", "1 pieza", "3 volumes".
 *  A janela de até duas palavras entre o número e o volume cobre o adjetivo
 *  ("2 checked bags") e BLOQUEIA a unidade de peso: em "23 kg por peça" o 23 é
 *  o quilo de CADA peça (o `KG POR PEÇA` que o gateway já documenta), e sem a
 *  guarda ele viraria "23 peças". */
const PIECES = new RegExp(
  String.raw`(\d+)\s*(?:(?!${KILO}\b)[a-z]+\s+){0,2}?(?:pecas?|piezas?|pieces?|volumes?|bags?|maletas?|malas?)\b`,
);
/** "23kg", "23 Kg", "23 quilos", "23 kilograms", "23,5 kg" — a unidade é
 *  OBRIGATÓRIA, senão o número da quantidade seria lido como peso. */
const WEIGHT = new RegExp(String.raw`(\d+(?:[.,]\d+)?)\s*${KILO}\b`);

/** Zero é NÃO-PREENCHIDO, não "zero peças" — a mesma leitura que o `luggageRule`
 *  do gateway já faz (a Copa manda `weight: 0` numa tarifa de 23kg). */
function positive(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const value = Number(raw.replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function lineOf(detail: TariffDetailLike): BaggageAllowanceLine {
  const text = detail.description?.trim() || null;
  const n = text === null ? '' : norm(text);
  return {
    included: includedFrom(detail.availability),
    pieces: positive(PIECES.exec(n)?.[1]),
    weightKg: positive(WEIGHT.exec(n)?.[1]),
    text,
  };
}

/**
 * As duas franquias (despacho e mão) que o array declara. `null` quando ele não
 * traz linha de bagagem nenhuma — as regras tarifárias (REFUND/CHANGE/CANCEL)
 * dividem o mesmo array e não afirmam nada sobre bagagem.
 *
 * A PRIMEIRA linha de cada tipo ganha: um array com duas linhas de despacho
 * descreve a mesma franquia duas vezes, e escolher a segunda não seria mais
 * verdadeiro do que escolher a primeira.
 */
export function deriveBaggageAllowance(
  details: readonly TariffDetailLike[] | null | undefined,
): BaggageAllowance | null {
  let checked: BaggageAllowanceLine | null = null;
  let carry: BaggageAllowanceLine | null = null;
  for (const detail of details ?? []) {
    const kind = kindOf(detail.name);
    if (kind === 'checked') checked ??= lineOf(detail);
    else if (kind === 'carry') carry ??= lineOf(detail);
  }
  return checked === null && carry === null ? null : { checked, carry };
}
